import React, { useMemo, useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { AdminApprovalRequestDetailView, AdminApprovalRequestView } from '../../../../services/admin-approval-requests/admin-approval-requests.service';
import { asCurrency, asHour } from '../../shared/utils/dashboard-page.utils';
import { SectionKey, WaiterActionBreakdownRow } from '../../shared/types/dashboard-page.types';
import classes from './waiter-issues-section.module.scss';

export type WaiterIssuesSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  waiterActionBreakdown: WaiterActionBreakdownRow[];
  waiterActionRows: AdminApprovalRequestView[];
  approvalDetailsById: Record<number, AdminApprovalRequestDetailView>;
};

type ActionTileKey = 'onTheHouse' | 'cancel' | 'itemDiscount' | 'globalDiscount';

const ACTION_TYPE_BY_TILE: Record<ActionTileKey, AdminApprovalRequestView['actionType']> = {
  onTheHouse: 'on_the_house',
  cancel: 'cancel',
  itemDiscount: 'item_discount',
  globalDiscount: 'global_discount',
};

const ACTION_LABELS: Record<ActionTileKey, string> = {
  onTheHouse: 'OTH',
  cancel: 'ביטולים',
  itemDiscount: 'הנחות פריט',
  globalDiscount: 'הנחות כלליות',
};

const formatTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--:--';
  }
  return asHour(value);
};

const getShortOrderCode = (orderCode: string | null): string => {
  if (!orderCode) {
    return '—';
  }
  const chunks = orderCode.split('/');
  if (chunks.length >= 3) {
    return `${chunks[chunks.length - 2]}/${chunks[chunks.length - 1]}`;
  }
  return orderCode;
};

export const WaiterIssuesSection: React.FC<WaiterIssuesSectionProps> = ({
  collapsedSections,
  toggleSection,
  waiterActionBreakdown,
  waiterActionRows,
  approvalDetailsById,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIssue, setSelectedIssue] = useState<{ waiterName: string; actionKey: ActionTileKey } | null>(null);

  const visibleWaiters = useMemo(
    () => waiterActionBreakdown.filter((row) => row.name.toLowerCase().includes(search.toLowerCase())),
    [waiterActionBreakdown, search],
  );

  const selectedRows = useMemo(() => {
    if (!selectedIssue) {
      return [];
    }
    return waiterActionRows
      .filter((row) => row.requestedByName === selectedIssue.waiterName)
      .filter((row) => row.actionType === ACTION_TYPE_BY_TILE[selectedIssue.actionKey])
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [selectedIssue, waiterActionRows]);

  const openIssueModal = (waiterName: string, actionKey: ActionTileKey) => {
    setSelectedIssue({ waiterName, actionKey });
  };

  const getItemsLabel = (row: AdminApprovalRequestView): React.ReactNode => {
    const detail = approvalDetailsById[row.id];
    if (!detail || detail.orderlines.length === 0) {
      return '—';
    }

    const quantityByName = new Map<string, number>();
    const orderlineNameById = new Map<number, string>();
    detail.orderlines.forEach((line) => {
      orderlineNameById.set(line.orderlineId, line.productName?.trim() || 'פריט כללי');
    });

    const payloadActionType = detail.payload.actionType;
    if (payloadActionType === 'cancel' || payloadActionType === 'item_discount' || payloadActionType === 'on_the_house') {
      detail.payload.items.forEach((item) => {
        const name = orderlineNameById.get(item.orderlineId);
        if (!name) {
          return;
        }
        quantityByName.set(name, (quantityByName.get(name) ?? 0) + item.quantity);
      });
    } else if (payloadActionType === 'general_item') {
      quantityByName.set('פריט כללי', detail.payload.quantity);
    } else {
      detail.orderlines.forEach((line) => {
        const name = line.productName?.trim() || 'פריט כללי';
        quantityByName.set(name, (quantityByName.get(name) ?? 0) + line.quantity);
      });
    }

    if (quantityByName.size === 0) {
      return '—';
    }

    const items = [...quantityByName.entries()];
    return items.map(([name, qty], index) => (
      <React.Fragment key={`${name}-${index}`}>
        {index > 0 ? ' · ' : ''}
        <span className={classes.modalItemEntry}>
          <span>{name}</span>
          {qty > 1 ? <span className={classes.modalItemQty}>{`${qty} x`}</span> : null}
        </span>
      </React.Fragment>
    ));
  };

  return (
    <>
      <SectionCard
        title="בעיות תפעול - לפי מלצר"
        sectionKey="waiterIssues"
        collapsed={collapsedSections.waiterIssues}
        onToggle={() => toggleSection('waiterIssues')}
        titleExtra={<span className={classes.countBadge}>{`(${waiterActionBreakdown.length} מלצרים)`}</span>}
      >
        <TextInput
          className={classes.searchInput}
          placeholder="חיפוש לפי שם מלצר..."
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          data-testid="dashboard-waiter-search"
        />

        {visibleWaiters.length === 0 ? (
          <p className={classes.emptyHint}>אין נתונים להצגה</p>
        ) : (
          <div className={classes.list}>
            {visibleWaiters.map((row) => (
              <div key={row.name} className={classes.card}>
                <div className={classes.cardHeader}>
                  <span className={classes.waiterName}>{row.name}</span>
                  <span className={classes.costBadge}>{`עלות: ${asCurrency(row.totalCost)}`}</span>
                </div>
                <div className={classes.grid}>
                  <Button
                    variant="cancel"
                    className={classes.tileButton}
                    ariaLabel={`${row.name} - ${ACTION_LABELS.onTheHouse}`}
                    disabled={row.onTheHouse.count === 0}
                    onClick={() => openIssueModal(row.name, 'onTheHouse')}
                    dataTestId={`dashboard-waiter-tile-onTheHouse-${row.name}`}
                  >
                    <div className={`${classes.tile} ${classes.tileOnTheHouse}`}>
                      <span className={classes.tileLabel}>OTH</span>
                      <span className={classes.tileCount}>{row.onTheHouse.count}</span>
                      <span className={row.onTheHouse.cost > 0 ? classes.tileCost : `${classes.tileCost} ${classes.tileCostHidden}`}>
                        {row.onTheHouse.cost > 0 ? asCurrency(row.onTheHouse.cost) : '0'}
                      </span>
                    </div>
                  </Button>
                  <Button
                    variant="cancel"
                    className={classes.tileButton}
                    ariaLabel={`${row.name} - ${ACTION_LABELS.cancel}`}
                    disabled={row.cancel.count === 0}
                    onClick={() => openIssueModal(row.name, 'cancel')}
                    dataTestId={`dashboard-waiter-tile-cancel-${row.name}`}
                  >
                    <div className={`${classes.tile} ${classes.tileCancel}`}>
                      <span className={classes.tileLabel}>ביטולים</span>
                      <span className={classes.tileCount}>{row.cancel.count}</span>
                      <span className={row.cancel.cost > 0 ? classes.tileCost : `${classes.tileCost} ${classes.tileCostHidden}`}>
                        {row.cancel.cost > 0 ? asCurrency(row.cancel.cost) : '0'}
                      </span>
                    </div>
                  </Button>
                  <Button
                    variant="cancel"
                    className={classes.tileButton}
                    ariaLabel={`${row.name} - ${ACTION_LABELS.itemDiscount}`}
                    disabled={row.itemDiscount.count === 0}
                    onClick={() => openIssueModal(row.name, 'itemDiscount')}
                    dataTestId={`dashboard-waiter-tile-itemDiscount-${row.name}`}
                  >
                    <div className={`${classes.tile} ${classes.tileItemDiscount}`}>
                      <span className={classes.tileLabel}>הנחות פריט</span>
                      <span className={classes.tileCount}>{row.itemDiscount.count}</span>
                      <span className={row.itemDiscount.cost > 0 ? classes.tileCost : `${classes.tileCost} ${classes.tileCostHidden}`}>
                        {row.itemDiscount.cost > 0 ? asCurrency(row.itemDiscount.cost) : '0'}
                      </span>
                    </div>
                  </Button>
                  <Button
                    variant="cancel"
                    className={classes.tileButton}
                    ariaLabel={`${row.name} - ${ACTION_LABELS.globalDiscount}`}
                    disabled={row.globalDiscount.count === 0}
                    onClick={() => openIssueModal(row.name, 'globalDiscount')}
                    dataTestId={`dashboard-waiter-tile-globalDiscount-${row.name}`}
                  >
                    <div className={`${classes.tile} ${classes.tileGlobalDiscount}`}>
                      <span className={classes.tileLabel}>הנחות כלליות</span>
                      <span className={classes.tileCount}>{row.globalDiscount.count}</span>
                      <span className={row.globalDiscount.cost > 0 ? classes.tileCost : `${classes.tileCost} ${classes.tileCostHidden}`}>
                        {row.globalDiscount.cost > 0 ? asCurrency(row.globalDiscount.cost) : '0'}
                      </span>
                    </div>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Modal
        opened={Boolean(selectedIssue)}
        onClose={() => setSelectedIssue(null)}
        centered
        size="lg"
        title={selectedIssue ? `${selectedIssue.waiterName} - ${ACTION_LABELS[selectedIssue.actionKey]}` : ''}
        classNames={{
          content: classes.modalContent,
          header: classes.modalHeader,
          title: classes.modalTitle,
          close: classes.modalClose,
          body: classes.modalBody,
        }}
        closeButtonProps={{ 'data-testid': 'dashboard-waiter-modal-close' } as Record<string, string>}
      >
        {selectedRows.length === 0 ? (
          <p className={classes.modalEmpty}>אין אירועים להצגה</p>
        ) : (
          <div className={classes.modalList}>
            {selectedRows.map((row) => {
              return (
                <div key={row.id} className={classes.modalCard}>
                  <div className={classes.modalGrid}>
                    <div className={classes.modalCell}>
                      <span className={classes.modalCellLabel}>שעה</span>
                      <span className={classes.modalCellValue}>{formatTime(row.createdAt)}</span>
                    </div>
                    <div className={classes.modalCell}>
                      <span className={classes.modalCellLabel}>הזמנה</span>
                      <span className={classes.modalCellValue}>{getShortOrderCode(row.orderCode)}</span>
                    </div>
                    <div className={classes.modalCell}>
                      <span className={classes.modalCellLabel}>פריטים</span>
                      <span className={`${classes.modalCellValue} ${classes.modalItemsValue}`}>{getItemsLabel(row)}</span>
                    </div>
                    <div className={classes.modalCell}>
                      <span className={classes.modalCellLabel}>עלות</span>
                      <span className={`${classes.modalCellValue} ${classes.modalCost}`}>{asCurrency(row.cost)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
};
